import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Play, Pause, X, Lock, Eye } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useWorkspacePermissions } from "@/hooks/use-workspace-permissions";
import { importOpen, useCampaignAction, useCampaigns } from "@/services/campaign.query";
import type { Campaign, CampaignAction } from "@/services/campaign.service";
import { useCampaignAccess } from "@/hooks/use-workspace-subscription";
import CreateCampaignDialog from "@/components/BatchCall/CreateCampaignDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";

const STATUS_BADGE: Record<Campaign["status"], "default" | "secondary" | "destructive"> = {
  draft: "secondary",
  scheduled: "default",
  warmup: "default",
  "in-progress": "default",
  paused: "secondary",
  dormant: "secondary",
  completed: "secondary",
  cancelled: "destructive",
};

/** A draft starts once its contacts are in: a campaign with no import (made before v2) has them already. */
const startable = (campaign: Campaign) => !campaign.import || campaign.import.status === "done";

/** Where a campaign's contacts import is, while it is not done: importing (with how far), or failed. */
function ImportBadge({ campaign }: { campaign: Campaign }) {
  const file = campaign.import;
  if (file?.status === "failed") return <Badge variant="destructive">Import failed</Badge>;
  if (!file || !importOpen(campaign)) return null;
  const progress = file.total_lines ? ` ${file.lines_read.toLocaleString("en-US")} of ${file.total_lines.toLocaleString("en-US")}` : "";
  return <Badge variant="outline">Importing…{progress}</Badge>;
}

export default function BatchCall() {
  const { currentWorkspaceId } = useWorkspace();
  const { canEdit } = useWorkspacePermissions();
  const { data: campaigns, isLoading } = useCampaigns(currentWorkspaceId || undefined);
  const campaignAction = useCampaignAction();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const { hasAccess, isLoading: isLoadingAccess } = useCampaignAccess();
  const navigate = useNavigate();

  const getStatusColor = (status: Campaign["status"]) => STATUS_BADGE[status];

  const handleAction = (campaignId: string, action: CampaignAction) => {
    campaignAction.mutate({ id: campaignId, action });
  };

  // Show loading state
  if (isLoadingAccess) {
    return (
      <div className="p-8">
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-4 w-96 mb-8" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  // Show upgrade prompt if no subscription access
  if (!hasAccess) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold mb-1">Batch Call Campaigns</h1>
            <p className="text-sm text-muted-foreground">
              Create and manage outbound call campaigns with AI agents
            </p>
          </div>
        </div>
        
        <Card className="p-12 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="p-4 rounded-full bg-muted">
              <Lock className="h-8 w-8 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-semibold">Subscription Required</h2>
            <p className="text-muted-foreground max-w-md">
              The Campaign feature is available exclusively for Pro subscribers. 
              Upgrade your subscription to unlock batch calling capabilities with 
              increased concurrency limits and advanced campaign management.
            </p>
            <div className="flex gap-3 mt-4">
              <Button onClick={() => navigate(`/${currentWorkspaceId}/billing`)}>
                Upgrade to Pro
              </Button>
              <Button variant="outline" onClick={() => navigate(`/${currentWorkspaceId}/agents`)}>
                Return to Agents
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[calc(100vh-4rem)]">
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold mb-1">Batch Call Campaigns</h1>
            <p className="text-sm text-muted-foreground">
              Create and manage outbound call campaigns with AI agents
            </p>
          </div>
          {canEdit && (
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New Campaign
            </Button>
          )}
        </div>

        {isLoading ? (
        <div className="text-center text-muted-foreground py-12">Loading...</div>
      ) : campaigns && campaigns.length > 0 ? (
        <div className="grid gap-4">
          {campaigns.map((campaign) => {
            const progressPercent = campaign.total_contacts > 0 
              ? (campaign.completed_calls / campaign.total_contacts) * 100 
              : 0;
            
            return (
              <Card 
                key={campaign.id} 
                className="p-6 cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => navigate(`/${currentWorkspaceId}/batch-call/${campaign.id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-3 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-lg">{campaign.campaign_name}</h3>
                      <Badge variant={getStatusColor(campaign.status)}>
                        {campaign.status}
                      </Badge>
                      <ImportBadge campaign={campaign} />
                    </div>
                    
                    {/* Progress bar */}
                    {campaign.status !== 'draft' && (
                      <div className="max-w-md">
                        <div className="flex justify-between text-xs text-muted-foreground mb-1">
                          <span>{campaign.completed_calls} / {campaign.total_contacts} completed</span>
                          <span>{progressPercent.toFixed(0)}%</span>
                        </div>
                        <Progress value={progressPercent} className="h-2" />
                      </div>
                    )}
                    
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Total Contacts</p>
                        <p className="font-medium">{campaign.total_contacts}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Completed</p>
                        <p className="font-medium">{campaign.completed_calls}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Successful</p>
                        <p className="font-medium text-green-600">{campaign.successful_calls}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Failed</p>
                        <p className="font-medium text-red-600">{campaign.failed_calls}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/${currentWorkspaceId}/batch-call/${campaign.id}`)}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Details
                    </Button>
                    {canEdit && campaign.status === 'draft' && (
                      <Button
                        size="sm"
                        onClick={() => handleAction(campaign.id, 'start')}
                        disabled={campaignAction.isPending || !startable(campaign)}
                        title={startable(campaign) ? undefined : "The contacts are still importing"}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Start
                      </Button>
                    )}
                    {canEdit && (campaign.status === 'in-progress' ||
                      campaign.status === 'warmup' ||
                      campaign.status === 'dormant') && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAction(campaign.id, 'pause')}
                        disabled={campaignAction.isPending}
                      >
                        <Pause className="w-4 h-4 mr-2" />
                        Pause
                      </Button>
                    )}
                    {canEdit && campaign.status === 'paused' && (
                      <Button
                        size="sm"
                        onClick={() => handleAction(campaign.id, 'resume')}
                        disabled={campaignAction.isPending}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Resume
                      </Button>
                    )}
                    {canEdit && (campaign.status === 'draft' ||
                      campaign.status === 'scheduled' ||
                      campaign.status === 'warmup' ||
                      campaign.status === 'in-progress' ||
                      campaign.status === 'paused' ||
                      campaign.status === 'dormant') && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleAction(campaign.id, 'cancel')}
                        disabled={campaignAction.isPending}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-96 border-2 border-dashed border-border rounded-lg">
          <p className="text-muted-foreground mb-4">No campaigns yet</p>
          {canEdit && (
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Campaign
            </Button>
          )}
        </div>
      )}

        {currentWorkspaceId && (
          <CreateCampaignDialog
            open={showCreateDialog}
            onOpenChange={setShowCreateDialog}
            workspaceId={currentWorkspaceId}
          />
        )}
      </div>
    </ScrollArea>
  );
}
